import { GuildShield } from '@/atoms/Icons/GuildShield'
import { Image } from '@/atoms/Image'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { AsDiv, CaptionXS } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { AnyTierId, formatShortPlanNameDefaultBasicWithAds } from './tiers'

export function CardHeroGraphic(props: {
  salutation?: string
  message?: string
  salutant?: string
  tier?: AnyTierId | null
}) {
  const { t } = useTranslate('guild')
  const salutation = props.salutation ?? t('yourFriend', 'Your Friend')
  const message =
    props.message ??
    t(
      'iWantedToShareAngel',
      'I wanted to share our love of Angel with you. Please enjoy this gift from our family to you. Thank you for all you do.',
    )
  const salutant = props.salutant ?? t('yourName', 'Your Name')
  return (
    <div className="relative">
      <Image
        src={getCloudinaryImageUrl({
          path: '/v1763597546/angel-studios/guild/gift/envelope2.webp',
        })}
        alt=""
        width={418}
        height={406}
        className="aspect-[406/418] absolute right-0 top-0 h-[240px] w-[247px] -translate-y-8 translate-x-[30%] md:h-[418px] md:w-[406px] md:-translate-y-16 md:translate-x-0"
      />
      <div className="relative bg-white p-3 md:-translate-x-1/2 md:p-6">
        <div className="border-2 border-[#FBD780]">
          <div className="m-1 w-[190px] border border-[#FBD780] p-2 md:w-[246px] md:p-4">
            <div className="relative mb-6 w-full bg-gradient-152 p-3 md:p-4">
              <StickyTab className="absolute -left-2 -top-2" />
              <StickyTab className="absolute -right-2 -top-2 rotate-90" />
              <StickyTab className="absolute -bottom-2 -right-2 rotate-180" />
              <StickyTab className="absolute -bottom-2 -left-2 rotate-[270deg]" />
              <div className="mb-3 flex items-start justify-between">
                <AngelLogo color="black" />
                <GuildShield />
              </div>
              <CaptionXS weight="bold" as={AsDiv} className="md:photon-caption-sm">
                <MembershipName tier={props.tier} />
              </CaptionXS>
              <CaptionXS as={AsDiv} className="md:photon-caption-sm mb-6">
                {t('recurringGift', 'Recurring gift')}
              </CaptionXS>
              <CaptionXS weight="bold" as={AsDiv} className="md:photon-caption-sm">
                <Translate t={t} i18nKey="toName" values={{ salutation }}>
                  To: {{ salutation }}
                </Translate>
              </CaptionXS>
            </div>

            <div className="px-2 font-handwriting text-[12px] md:px-0 md:text-[16px]">
              <div>
                <Translate t={t} i18nKey="dearName" values={{ salutation }}>
                  Dear {{ salutation }},
                </Translate>
              </div>
              <div className="mb-2">{message}</div>
              <div>
                <Translate t={t} i18nKey="fromName" values={{ salutant }}>
                  From: {{ salutant }}
                </Translate>
              </div>
            </div>
          </div>
        </div>
        <Image
          src={getCloudinaryImageUrl({
            path: '/v1763658784/angel-studios/guild/gift/red-bow.png',
          })}
          alt=""
          width={108}
          height={108}
          className="absolute right-0 top-0 h-[64px] w-[64px] -translate-y-[35%] translate-x-[35%] md:h-[108px] md:w-[108px]"
        />
        <Image
          src={getCloudinaryImageUrl({
            path: '/v1763589531/angel-studios/guild/gift/tali-only.webp',
          })}
          alt=""
          width={0}
          height={0}
          className="absolute bottom-0 left-0 h-[150px] w-[109px] -translate-x-[68%] translate-y-[20%] object-contain md:left-auto md:right-0 md:h-[244px] md:w-[197px] md:translate-x-[54%] md:translate-y-0"
        />
        <Image
          src={getCloudinaryImageUrl({
            path: '/v1763589531/angel-studios/guild/gift/david-only.webp',
          })}
          alt=""
          width={0}
          height={0}
          className="absolute bottom-0 right-0 h-[228px] w-[139px] translate-x-[70%] translate-y-[14%] object-contain md:h-[430px] md:w-[320px] md:translate-x-full md:translate-y-[5%]"
        />
      </div>
    </div>
  )
}

function StickyTab(props: React.ComponentProps<'svg'>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={28} height={28} fill="none" {...props}>
      <ellipse
        cx={13.293}
        cy={14.292}
        fill="url(#sticky-a)"
        rx={4.877}
        ry={16.75}
        transform="rotate(45.002 13.293 14.292)"
      />
      <path fill="url(#sticky-b)" d="M0 28V0h28z" />
      <defs>
        <radialGradient
          id="sticky-a"
          cx={0}
          cy={0}
          r={1}
          gradientTransform="matrix(6.0913 -.00055 -.01997 20.9211 10.33 13.783)"
          gradientUnits="userSpaceOnUse"
        >
          <stop />
          <stop offset={1} stopColor="#737373" stopOpacity={0} />
        </radialGradient>
        <radialGradient
          id="sticky-b"
          cx={0}
          cy={0}
          r={1}
          gradientTransform="rotate(226.736 8.592 3.408)scale(11.6726)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#f2f2f2" />
          <stop offset={1} stopColor="#fff" />
        </radialGradient>
      </defs>
    </svg>
  )
}

function MembershipName(props: { tier: AnyTierId | null | undefined }) {
  const { t } = useTranslate('guild')

  const planName = formatShortPlanNameDefaultBasicWithAds(t, props.tier)
  return (
    <Translate t={t} i18nKey="planAngelMembership" values={{ planName }}>
      {{ planName }} Angel Membership
    </Translate>
  )
}
